import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBand } from "@/components/CtaBand";
import { BRAND, IMAGES } from "@/config/brand";
import { POSTS } from "@/pages/MovingTips";

const content = {
    "moving-house-checklist": [
        ["6 weeks before", "Declutter room by room. Book your removals company early to secure your preferred date."],
        ["4 weeks before", "Notify utilities, banks and the electoral roll. Start using up freezer food."],
        ["2 weeks before", "Begin packing non-essentials. Reserve parking or permits at both properties."],
        ["1 week before", "Confirm details with your removals team. Pack an essentials box for day one."],
        ["Moving day", "Have documents and valuables with you. Do a final walk-through before leaving."],
    ],
    "how-to-prepare-for-moving-day": [
        ["The night before", "Charge phones, stack labelled boxes near the door, and lay out warm clothes."],
        ["Morning of", "Take meter readings, empty the fridge, and be ready 15 minutes before your slot."],
        ["During the move", "Point out fragile boxes and unusual items. Keep pets and children in a calm space."],
        ["At the new home", "Direct boxes by room labels. Test appliances and note any concerns while the team is there."],
    ],
    "how-to-pack-fragile-items": [
        ["Layer the box", "Line the bottom with crumpled paper or bubble wrap for shock absorption."],
        ["Wrap individually", "Wrap glasses, plates and ornaments one at a time; secure with tape."],
        ["Fill the gaps", "Pack tightly — items should not shift when the box is gently shaken."],
        ["Label clearly", "Mark boxes ‘Fragile — this way up’ on all sides."],
    ],
    "how-much-do-london-removals-cost": [
        ["Volume", "The number and size of items is the biggest driver of cost."],
        ["Access", "Stairs, floor level, distance to the van and permit-only parking all add time."],
        ["Distance", "Cross-London and out-of-town journeys add travel time."],
        ["Extras", "Packing help, materials, dismantling and short-notice bookings may add to the price."],
    ],
    "parking-and-access-for-a-london-move": [
        ["Reserve space", "Where possible, mark out a suitable space with your neighbours’ help."],
        ["Permits", "Some boroughs require suspension permits — apply early to avoid delays."],
        ["Narrow roads", "Let us know if the road is narrow or has restricted access hours."],
        ["Buildings", "Reserve lift use and confirm access windows with concierge if applicable."],
    ],
    "questions-to-ask-a-removals-company": [
        ["How is the quotation calculated?", "Look for clear, itemised quotations without surprise charges."],
        ["Do you provide packing materials?", "Ask what’s included and what costs extra."],
        ["How do you protect furniture?", "Blankets, corner guards and shrink-wrap indicate careful handling."],
        ["What happens if the move takes longer?", "Understand how any additional time is billed before the day."],
    ],
};

export default function BlogPost() {
    const { slug } = useParams();
    const post = POSTS.find((p) => p.slug === slug);
    if (!post) {
        return (
            <div className="container-ys section">
                <h1 className="text-2xl font-semibold">Article not found</h1>
                <Link to="/moving-tips" className="text-[#001B3D]">Back to moving tips</Link>
            </div>
        );
    }
    const sections = content[slug] || [];
    const ldJson = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        author: BRAND.name,
        publisher: { "@type": "Organization", name: BRAND.name },
    };
    return (
        <div>
            <PageMeta title={`${post.title} — YS Removals`} description={post.excerpt} canonical={`https://${BRAND.domain}/moving-tips/${slug}`} ldJson={ldJson} />
            <section className="bg-[#001B3D] text-white">
                <div className="container-ys section">
                    <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Moving Tips", to: "/moving-tips" }, { label: post.title }]} />
                    <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight font-heading">{post.title}</h1>
                    <p className="mt-3 text-white/85 max-w-2xl">{post.excerpt}</p>
                </div>
            </section>
            <section className="container-ys section grid lg:grid-cols-3 gap-8">
                <article className="lg:col-span-2 space-y-6">
                    <div className="rounded-2xl overflow-hidden border border-[#17252D]/10">
                        <img src={IMAGES.blog1} alt="" className="w-full h-full object-cover aspect-[16/9]" />
                    </div>
                    {sections.map(([heading, body], i) => (
                        <div key={i}>
                            <h2 className="text-2xl font-heading font-semibold">{heading}</h2>
                            <p className="mt-2 text-[#17252D]/85 leading-relaxed">{body}</p>
                        </div>
                    ))}
                </article>
                <aside className="space-y-3">
                    <div className="rounded-xl border border-[#17252D]/10 p-4">
                        <div className="text-sm font-semibold">More guides</div>
                        <ul className="mt-2 space-y-2 text-sm">
                            {POSTS.filter((p) => p.slug !== slug).slice(0, 5).map((p) => (
                                <li key={p.slug}><Link to={`/moving-tips/${p.slug}`} className="text-[#001B3D] hover:underline">{p.title}</Link></li>
                            ))}
                        </ul>
                    </div>
                </aside>
            </section>
            <CtaBand />
        </div>
    );
}
